import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Box, TextField, Button, Typography, Link } from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import { useAuthFormToast } from '../components/auth/useAuthFormToast';
import {
  AUTH_PRIMARY,
  authLinkSx,
  authPrimaryButtonSx,
  otpBoxSx,
} from '../components/auth/authFormStyles';
import { getPendingVerificationEmailStorage } from '../utils/pendingVerification';
import { deriveProfileComplete } from '../utils/profileCompletion';
import { clearPendingSignupDraft } from '../utils/pendingSignupDraft';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

export const OtpVerifyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    pendingVerificationEmail,
    setPendingVerificationEmail,
    clearPendingVerificationEmail,
    verifyOtpAndLogin,
    isAuthenticated,
  } = useAuth();

  const stateEmail = (location.state as { email?: string } | null)?.email ?? null;

  const verificationEmail = useMemo(
    () =>
      pendingVerificationEmail ||
      stateEmail ||
      getPendingVerificationEmailStorage(),
    [pendingVerificationEmail, stateEmail]
  );

  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess, ToastHost } = useAuthFormToast();
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [resending, setResending] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const otp = digits.join('');

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  useEffect(() => {
    if (verificationEmail && verificationEmail !== pendingVerificationEmail) {
      setPendingVerificationEmail(verificationEmail);
    }
  }, [verificationEmail, pendingVerificationEmail, setPendingVerificationEmail]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  if (!verificationEmail && !isAuthenticated) {
    return <Navigate to="/signup" replace />;
  }

  const handleSubmit = async (code: string = otp) => {
    if (code.length !== OTP_LENGTH) return;
    setLoading(true);
    try {
      const clientData = await verifyOtpAndLogin(verificationEmail as string, code);
      clearPendingVerificationEmail();
      clearPendingSignupDraft();
      if (!deriveProfileComplete(clientData)) {
        navigate('/complete-profile', { replace: true });
      } else {
        navigate('/');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid code. Please try again.';
      showError(message);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await authService.resendOtp(verificationEmail as string);
      setSecondsLeft(RESEND_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
      showSuccess('A new code has been sent to your email.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to resend code';
      showError(message);
    } finally {
      setResending(false);
    }
  };

  const updateDigit = (index: number, value: string) => {
    const char = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = char;
    setDigits(next);

    if (char && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    const code = next.join('');
    if (char && index === OTP_LENGTH - 1 && code.length === OTP_LENGTH) {
      handleSubmit(code);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;

    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => {
      next[i] = char;
    });
    setDigits(next);

    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();

    if (pasted.length === OTP_LENGTH) {
      handleSubmit(pasted);
    }
  };

  const handleUseDifferentEmail = () => {
    clearPendingVerificationEmail();
    navigate('/signup', { state: { changeEmail: true } });
  };

  return (
    <>
    <AuthPageShell
      title="Verify your email"
      subtitle={`Enter the 6-digit code we sent to ${verificationEmail}`}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: '#FFF5F5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        <MarkEmailReadOutlinedIcon sx={{ color: AUTH_PRIMARY, fontSize: 28 }} />
      </Box>

      <Box
        sx={{ display: 'flex', gap: { xs: 0.75, sm: 1 }, mb: 3, justifyContent: 'center' }}
        onPaste={handlePaste}
      >
        {digits.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            value={digit}
            onChange={(e) => updateDigit(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onFocus={() => setFocusedIndex(index)}
            sx={otpBoxSx(Boolean(digit), focusedIndex === index)}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              autoComplete: index === 0 ? 'one-time-code' : 'off',
              'aria-label': `Digit ${index + 1}`,
            }}
          />
        ))}
      </Box>

      <Button
        fullWidth
        variant="contained"
        disabled={loading || otp.length !== OTP_LENGTH}
        onClick={() => handleSubmit(otp)}
        sx={{ ...authPrimaryButtonSx, mb: 2 }}
      >
        {loading ? 'Verifying…' : 'Verify & continue'}
      </Button>

      <Button
        fullWidth
        variant="text"
        disabled={secondsLeft > 0 || resending}
        onClick={handleResend}
        sx={{ mb: 2, color: '#565959', textTransform: 'none' }}
      >
        {resending
          ? 'Sending…'
          : secondsLeft > 0
            ? `Resend code in ${secondsLeft}s`
            : 'Resend code'}
      </Button>

      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
        Check your spam folder if you don&apos;t see the email.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
        You can also click the magic link in the email to verify instantly.
      </Typography>

      <Link
        component="button"
        type="button"
        onClick={handleUseDifferentEmail}
        sx={{ ...authLinkSx, fontSize: '0.875rem', cursor: 'pointer' }}
      >
        ← Use a different email
      </Link>
    </AuthPageShell>
    <ToastHost />
    </>
  );
};
